import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { Box, Grid } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../../../routing/LangRouter.js";

//import redux store
import { saveTicketSelectedFilters } from "../../../../store/actions/index.js";

//import customized hooks
import useDateChange from "../../../../hooks/useDateChange.js";

//import utils
import { debouncer } from "../../../../utils/utility.js";

//import constants
import { IS_MOBILE } from "../../../../constants/breakpoints";
import BASE_URL from "../../../../constants/baseUrls.js";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import RangeDatePicker from "../../../common/range-date-picker/range-date-picker.jsx";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js";
import PersonCount from "../../../common/person-count/person-count";
import BaseCheckbox from "../../../common/base-checkbox/base-checkbox.jsx";
import BaseRadioButton from "../../../common/base-radio-button/base-radio-button.jsx";
import BaseTextField from "../../../common/base-text-field/base-text-field.jsx";

//import images and icons
import Icon from "../../../common/base-icon/icon.jsx";
import { ArrowSwapHorizontal, CloseCircle, Location, SearchNormal } from "iconsax-react";
import { CABIN_TYPE } from "../../../../constants/cabinType.js";

function SearchFormContent({ dispatch, setRequestFlag, setUid, intFlightRoute }) {
    //general variables
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { control } = useForm();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const destinationRef = useRef();
    const rangeDatePickerRef = useRef();

    //sessionStorage variables
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    const [showAutocompplete, setShowAutocompplete] = useState({ departure: IS_MOBILE ? false : true, destination: IS_MOBILE ? false : true });
    const handleShowAutocompplete = name => {
        setShowAutocompplete({ [name]: true });
    };

    //handle ticket type change (one way flight or return flight)
    const [ticketType, setTicketType] = useState(intFlightInformation?.returnDate === null ? "oneWay" : "return");
    const handleRadioChange = event => {
        setTicketType(event.target.value);
        handleDateFormatChange(null);

        if (!!intFlightInformation) {
            intFlightInformation.departureDate = null;
            intFlightInformation.returnDate = null;
            sessionStorage.setItem("intFlightInformation", JSON.stringify(intFlightInformation));
        }
    };

    //define variables for city list
    const [cityList, setCityList] = useState([]);
    const [cityLoading, setCityLoading] = useState(false);

    //request city list
    const getCityList = async input => {
        try {
            setCityLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/basic/intl/cities/`, {
                params: {
                    search: input,
                },
            });
            const cityList = [...response.data.data.results];
            setCityList(cityList);
            setCityLoading(false);
        } catch (error) {
            setCityLoading(false);
        }
    };

    //define variables for destination city list
    const [destinationCityList, setDestinationCityList] = useState([]);
    const [destinationCityLoading, setDestinationCityLoading] = useState(false);

    //request destination city list
    const getDestinationCityList = async input => {
        try {
            setDestinationCityLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/basic/intl/cities/`, {
                params: {
                    search: input,
                },
            });
            const cityList = [...response.data.data.results];
            setDestinationCityList(cityList);
            setDestinationCityLoading(false);
        } catch (error) {
            setDestinationCityLoading(false);
        }
    };

    //handle departure city change
    const onDepartureInputChange = useMemo(
        () =>
            debouncer(input => {
                setCityLoading(true);
                getCityList(input);
            }, 1000),
        [],
    );

    //handle destination city change
    const onDestinationInputValue = useMemo(
        () =>
            debouncer(input => {
                setDestinationCityLoading(true);
                getDestinationCityList(input);
            }, 1000),
        [],
    );

    //call request city list on page load
    useEffect(() => {
        if (location.pathname === `/${locale}/flight-search/international` || location.pathname === `/${locale}/international-flight`) {
            getCityList();
            getDestinationCityList();
        }
    }, [location.pathname]);

    //define variables for selected city
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);

    //handle departure and destination change
    const onDepartureChange = value => {
        setSelectedDeparture(value);
        if (IS_MOBILE) setShowAutocompplete({ departure: false, destination: selectedDestination || isItinerary ? false : true });
        if (!selectedDestination) destinationRef?.current?.focus();
        setUid("");
    };

    const onDestinationChange = value => {
        setSelectedDestination(value);
        if (IS_MOBILE) setShowAutocompplete({ departure: false, destination: false });
        if (!startDate && !endDate) rangeDatePickerRef?.current?.focus();
        setUid("");
    };

    //default value for departute & destination
    useEffect(() => {
        setSelectedDeparture(intFlightInformation?.departure || null);
        setSelectedDestination(intFlightInformation?.destination || null);
        setCabinType(intFlightInformation?.cabinType || CABIN_TYPE?.[0]);
    }, []);

    //handle swapping the departure and destination value
    const handleSwapChange = () => {
        setSelectedDestination(selectedDeparture);
        setSelectedDeparture(selectedDestination);
    };

    //define variable for date

    const [allPersonVisibility, setAllPersonVisibility] = useState(false);

    //handle hotel date change
    const initialStartDate = intFlightInformation?.departureDate;
    const initialEndDate = intFlightInformation?.returnDate;
    const { startDate, endDate, defaultValueDate, handleDateFormatChange } = useDateChange(
        initialStartDate,
        initialEndDate,
        ticketType === "return" ? true : false,
    );
    const dateChange = (dates, _, isPersianCalendar) => {
        handleDateFormatChange(dates, isPersianCalendar);
        setUid(null);
    };

    //list of person types with their details
    const personList = [
        { id: 1, label: t("adult"), description: t("ageGroup1"), name: "adult", disabled: 1, validation: true },
        { id: 2, label: t("child"), description: t("ageGroup2"), name: "child", disabled: 0, validation: true },
        { id: 2, label: t("infant"), description: t("ageGroup3"), name: "infant", disabled: 0, validation: true },
    ];

    //handle changes in the number of people
    const [personCounter, setPersonCounter] = useState({});
    const [defaultValuePerson, setDefaultValuePerson] = useState({});
    const handlePersonCountChange = ({ adult, child, infant, totalPersons }) => {
        if (totalPersons) setPersonCounter({ adult, child, infant, totalPersons });
        setUid("");
    };

    const [isDirect, setIsDirect] = useState(intFlightInformation?.isDirect || false);
    const handleDirectChange = event => {
        const { checked } = event.target;
        setIsDirect(checked);
    };

    //persons default value
    useEffect(() => {
        if (intFlightInformation)
            setDefaultValuePerson({
                adult: intFlightInformation.personCounter.adult ?? 1,
                child: intFlightInformation.personCounter.child ?? 0,
                infant: intFlightInformation.personCounter.infant ?? 0,
                totalPersons: intFlightInformation.personCounter.totalPersons ?? 1,
            });
    }, []);

    //remove selected ticket
    const removeSelectedTicket = () => {
        sessionStorage.removeItem("selectedIntFlight");
    };

    //handle flight class
    const [cabinType, setCabinType] = useState(CABIN_TYPE?.[0]);
    const handleFlightClassChange = value => {
        setCabinType(value);
        setUid("");
    };

    //save the new value in the sessionStorage
    const newSave = () => {
        const newIntFlightInformation = {
            departure: selectedDeparture,
            destination: selectedDestination,
            departureDate: startDate || intFlightInformation?.departureDate,
            returnDate: ticketType === "return" ? endDate || intFlightInformation?.returnDate : null,
            personCounter: personCounter,
            cabinType,
            isDirect,
        };
        sessionStorage.setItem("intFlightInformation", JSON.stringify(newIntFlightInformation));
    };

    //change the itinerary variables
    const itineraryVariables = () => {
        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //submit form
    const submitSearchForm = event => {
        event.preventDefault();
        resetFilters();
        // search form validation
        if (!selectedDeparture || !selectedDestination || !startDate) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });
        if (selectedDeparture?.id === selectedDestination?.id) return toast.error(t("errorMessage7"), { toastId: "custom-id-yes" });
        if (ticketType === "return" && !endDate) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });

        itineraryVariables();
        removeSelectedTicket();
        newSave();

        sessionStorage.removeItem("intFlightPassengers");
        sessionStorage.removeItem("selectedReservation");

        searchParams.delete("action");
        setSearchParams(searchParams);

        if (!intFlightRoute) setRequestFlag(true);
        if (!!intFlightRoute) navigate(intFlightRoute);
    };

    //list of sorting options
    const optionList = [
        { value: "return", label: `${t("roundTrip")}`, checked: true },
        { value: "oneWay", label: `${t("oneWay")}`, checked: false },
    ];

    return (
        <Grid
            component="form"
            className="flight-search-form"
            container
            item
            xs={12}
            rowGap={2.5}
            justifyContent="space-between"
            alignItems="center"
            onSubmit={submitSearchForm}
        >
            <Grid className="flight-search-form__ticket-type" container item xs={12} alignItems="center">
                <BaseRadioButton
                    name="ticketType"
                    value={ticketType}
                    optionList={optionList}
                    handleChange={handleRadioChange}
                    disabled={isItinerary ? true : false}
                />
            </Grid>

            <Grid className="flight-search-form__select" container item xs={12} md={3.8} justifyContent="space-between" alignItems="center">
                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("departure")}
                            value={locale === "en" ? selectedDeparture?.english_name : selectedDeparture?.name}
                            onClick={() => handleShowAutocompplete("departure")}
                            radius="10px"
                            icon={<Icon name="DepartureIcon" width="0.8rem" />}
                            readOnly={true}
                            disabled={isItinerary ? true : false}
                        />
                    </Grid>
                )}
                {showAutocompplete?.departure && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("departure")}
                                options={cityList}
                                name="departure"
                                defaultValue={selectedDeparture}
                                handleChange={onDepartureChange}
                                setInputValue={onDepartureInputChange}
                                control={control}
                                loading={cityLoading}
                                disabled={isItinerary ? true : false}
                                width={350}
                                icon={<Icon name="DepartureIcon" width="0.8rem" />}
                                open={IS_MOBILE ? true : false}
                                type="flight"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocompplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
                {!isItinerary ? (
                    <Box className="flight-search-form__swap">
                        <ArrowSwapHorizontal
                            size={IS_MOBILE ? "18" : "24"}
                            color="var(--light-color)"
                            onClick={selectedDeparture && selectedDestination && handleSwapChange}
                        />
                    </Box>
                ) : null}

                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("destination")}
                            value={locale === "en" ? selectedDestination?.english_name : selectedDestination?.name}
                            onClick={() => handleShowAutocompplete("destination")}
                            radius="10px"
                            icon={<Location size="20" color="var(--teal-color-18)" />}
                            disabled={isItinerary}
                            readOnly={true}
                        />
                    </Grid>
                )}
                {showAutocompplete?.destination && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("destination")}
                                options={destinationCityList}
                                name="destination"
                                defaultValue={selectedDestination}
                                handleChange={onDestinationChange}
                                setInputValue={onDestinationInputValue}
                                innerRef={destinationRef}
                                control={control}
                                loading={destinationCityLoading}
                                disabled={isItinerary ? true : false}
                                width={350}
                                icon={<Location size="20" color="var(--teal-color-18)" />}
                                open={IS_MOBILE ? true : false}
                                type="flight"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocompplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container item xs={12} md={3.8}>
                <RangeDatePicker
                    handleChange={dateChange}
                    defaultValue={defaultValueDate}
                    innerRef={rangeDatePickerRef}
                    labels={[t("departureDate"), t("returnDate")]}
                    minDate={new Date().setHours(0, 0, 0, 0)}
                    disableDate={false}
                    range={ticketType === "return" ? true : false}
                />
            </Grid>

            <Grid container item xs={5.8} md={1.25} alignItems="stretch">
                <PersonCount
                    list={personList}
                    handleChange={handlePersonCountChange}
                    menuVisibility={allPersonVisibility}
                    defaultValue={defaultValuePerson}
                />
            </Grid>
            <Grid container item xs={5.8} md={1.25} justifyContent="space-between">
                <AutoCompleteComponent
                    label={t("flightClass")}
                    options={CABIN_TYPE}
                    name="flightClass"
                    defaultValue={cabinType || CABIN_TYPE?.[0]}
                    handleChange={handleFlightClassChange}
                    // setInputValue={handleInputChange}
                    control={control}
                    disabled={false}
                    width={350}
                />
            </Grid>

            <Grid className="flight-search-form__direct" container item xs={5.8} md={1.25} alignItems="stretch">
                <BaseCheckbox label={t("Flight.Direct Flight")} checked={isDirect || false} handleChange={e => handleDirectChange(e)} />
            </Grid>

            <Grid container item md={12} justifyContent="flex-end">
                <Grid container item md={1}>
                    <BaseButton
                        text={`${t("search")}`}
                        color="primary-10"
                        size="large"
                        radius="radius-20"
                        type="submit"
                        leftIcon={<SearchNormal size="24" color="var(--primary-color)" />}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}
export default connect(mapStateToProps)(SearchFormContent);

function mapStateToProps(state) {
    return {
        ticketSort: state.ticketSort,
        ticketSelectedFilters: state.ticketSelectedFilters,
    };
}
